// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>

import './styles.css';
import { loadBundle, getBundle } from './data';
import { currentRoute, navigate, onRouteChange, DEFAULT_VIEW } from './router';
import { initTooltip } from './components/tooltip';
import { initGlossary } from './components/glossary';
import { closeAllOverlays } from './overlay';
import { openAbout } from './about';
import { esc, num } from './format';

import { renderSearch } from './views/search';
import { renderFlip } from './views/flip';
import { renderTaxonomy } from './views/taxonomy';
import { renderMatrix } from './views/matrix';
import { renderShelf } from './views/shelf';
import { renderSuppliers } from './views/suppliers';
import { renderNetwork } from './views/network';
import { renderChannels } from './views/channels';
import { renderVehicles } from './views/vehicles';

/** Nav labels are WORDS ONLY — never a count badge. Counts belong in the views. */
const VIEWS: { id: string; label: string; render: (host: HTMLElement) => void | Promise<void>; wide?: boolean }[] = [
  { id: 'search', label: 'Search', render: renderSearch },
  { id: 'trend', label: 'What changed', render: renderFlip, wide: true },
  { id: 'categories', label: 'What gets recalled', render: renderTaxonomy, wide: true },
  { id: 'matrix', label: 'Hazard by category', render: renderMatrix, wide: true },
  { id: 'shelf', label: 'Time on sale', render: renderShelf, wide: true },
  { id: 'suppliers', label: 'Repeat suppliers', render: renderSuppliers, wide: true },
  { id: 'retailers', label: 'Who sold it', render: renderNetwork, wide: true },
  { id: 'where', label: 'Where it sold', render: renderChannels, wide: true },
  { id: 'vehicles', label: 'Vehicles', render: renderVehicles, wide: true },
];

function shell(): { main: HTMLElement } {
  const app = document.getElementById('app')!;
  app.innerHTML = `
    <a class="skip-link" href="#main-content">Skip to content</a>
    <header class="site-header">
      <div class="header-inner">
        <a class="brand" href="#/${DEFAULT_VIEW}">
          <svg class="brand-mark" viewBox="0 0 64 64" aria-hidden="true">
            <rect width="64" height="64" rx="12" fill="#0f5c63"/>
            <path d="M14 24l18-8 18 8v22l-18 8-18-8z" fill="none" stroke="#fdfcfa" stroke-width="3.2" stroke-linejoin="round"/>
            <circle cx="45" cy="43" r="12.5" fill="#c2410c" stroke="#fdfcfa" stroke-width="3"/>
            <path d="M45 36.5v8.2" stroke="#fdfcfa" stroke-width="3.2" stroke-linecap="round"/>
            <circle cx="45" cy="49.6" r="1.9" fill="#fdfcfa"/>
          </svg>
          <span class="brand-name">Product Recalls</span>
        </a>
        <div class="header-spacer"></div>
        <button type="button" class="icon-btn" id="about-btn" aria-label="About this site and its limitations">About</button>
      </div>
    </header>
    <nav class="site-nav" aria-label="Views">
      <div class="nav-inner" id="nav-inner"></div>
    </nav>
    <main class="main-content" id="main-content" tabindex="-1"></main>
    <footer class="site-footer">
      <div class="footer-inner">
        <div class="footer-sources" id="footer-sources"></div>
        <div>Built by <a href="https://benrichardson.dev/">benrichardson.dev</a> · <a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">more tools &amp; sites</a></div>
      </div>
    </footer>
  `;

  const nav = app.querySelector<HTMLElement>('#nav-inner')!;
  for (const view of VIEWS) {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'nav-tab';
    b.textContent = view.label;
    b.dataset.view = view.id;
    b.addEventListener('click', () => navigate(view.id));
    nav.append(b);
  }

  app.querySelector<HTMLButtonElement>('#about-btn')!
    .addEventListener('click', () => openAbout(getBundle().meta));

  // The skip link must not reach the router. `href="#main-content"` sets
  // location.hash, which fires hashchange, which the router reads as the route
  // "main-content" — an unknown view, so it fell back to Search. Pressing "skip
  // to content" therefore threw the reader out of whatever view they were on.
  app.querySelector<HTMLAnchorElement>('.skip-link')!.addEventListener('click', (e) => {
    e.preventDefault();
    document.getElementById('main-content')?.focus();
  });

  return { main: app.querySelector<HTMLElement>('#main-content')! };
}

function markActive(id: string): void {
  for (const tab of document.querySelectorAll<HTMLElement>('.nav-tab')) {
    const on = tab.dataset.view === id;
    if (on) tab.setAttribute('aria-current', 'page');
    else tab.removeAttribute('aria-current');
    // Keep the active tab visible when the nav scrolls horizontally on a phone.
    if (on) tab.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }
}

async function route(main: HTMLElement): Promise<void> {
  // An overlay opened on one view must not survive into the next: on a phone a
  // drawer is full-width, so the reader would appear to be browsing behind it.
  closeAllOverlays();

  const { view } = currentRoute();
  const entry = VIEWS.find((v) => v.id === view) ?? VIEWS[0];
  markActive(entry.id);
  main.innerHTML = '';
  const wrap = document.createElement('div');
  wrap.className = `view-wrap${entry.wide ? '' : ' narrow'}`;
  main.append(wrap);
  try {
    await entry.render(wrap);
  } catch (err) {
    wrap.innerHTML = `<div class="error-state"><strong>This view failed to render.</strong>
      <p class="small">${esc(err instanceof Error ? err.message : String(err))}</p></div>`;
  }
}

async function boot(): Promise<void> {
  const { main } = shell();
  main.innerHTML = '<div class="view-wrap"><div class="loading">Loading the recall registers…</div></div>';

  try {
    const b = await loadBundle();
    document.querySelector('#footer-sources')!.innerHTML = `
      Consumer recalls from the <a href="https://www.productsafety.gov.au/recalls" target="_blank" rel="noopener">ACCC Product Safety register</a>
      (CC BY 4.0) · vehicle recalls from the <a href="https://www.vehiclerecalls.gov.au/" target="_blank" rel="noopener">Department of Infrastructure</a>
      · ${num(b.meta.accc.total)} consumer recalls and ${num(b.meta.vehicle.total)} vehicle campaigns, as at ${b.meta.asAt}.
      Independent project; not affiliated with either regulator.`;
  } catch (err) {
    main.innerHTML = `<div class="view-wrap"><div class="error-state">
      <strong>The recall data could not be loaded.</strong>
      <p class="small">${esc(err instanceof Error ? err.message : String(err))}</p>
      <p class="small">In the meantime, the official register is at
        <a href="https://www.productsafety.gov.au/recalls">productsafety.gov.au</a>.</p>
      <button type="button" class="btn" id="retry">Try again</button></div></div>`;
    document.getElementById('retry')?.addEventListener('click', () => location.reload());
    return;
  }

  initTooltip();
  initGlossary();
  if (!location.hash) history.replaceState(null, '', `#/${DEFAULT_VIEW}`);
  await route(main);
  onRouteChange(() => { void route(main); });
}

void boot();
